import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useLocation } from 'react-router-dom';
import { usePortalNavigate } from '@/hooks/usePortalPath';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import dayjs from 'dayjs';
import * as Yup from 'yup';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Classification,
  DayOfWeek,
  JobAcceptType,
  QuestionnaireInput,
  Site,
  User,
  Address,
  LatLng,
} from '@core/generated/gql-types';
import { formatShiftClockDurationHhMm } from '@core/util/datetime.util';
import { parseScheduleDateLocal } from '@core/util/jobScheduleBounds.util';
import { useJobWorkingDays } from '@core/job/workingDays/useJobWorkingDays';
import { useUserStore } from '@core/user/useUserStore';
import { useJobStore } from '@core/job/useJobStore';
import { useThemeStore } from '@core/theme/useThemeStore';
import { generateFriendlyAddress } from '@core/util/google-places.service';
import { Button } from '@/components';
import {
  ClientSelector,
  ClientSiteSelector,
  SkillSetSelector,
  DateSelector,
  WorkersCounter,
  TimeSelectors,
  WorkingDaysSelector,
  JobDescription,
  AssignmentMethod,
  AssignWorker,
  QuestionnaireSection,
} from '@/modules/jobs/components/create';
import DraftJobCard from '@/modules/jobs/components/DraftJobCard';
import { DraftJob } from '@/types/draftJob';
import { useTenantId } from '@/contexts/TenantContext';
import { isDateRangeOverlapping } from '@/utils/utils';
import {
  buildCreateJobInput,
  getJobCreateContractInstants,
  isShiftEndAfterStart,
} from '@/utils/createJobShiftValidation.util';
import { fetchUsersAvailableForJobCriteria } from '@core/availableWorkers/availableWorkers.action';
import { useCoreLoopConfigurationStore } from '@core/coreLoopConfiguration/useCoreLoopConfigurationStore';
import usePostingQuestionnaire from '@/hooks/usePostingQuestionnaire';
import useGenerateAiJobDescription from '@/hooks/useGenerateAiJobDescription';
import { useFeatureView } from '@/hooks';
import { getJobCreationView } from '../views/jobCreation.view';
import { useJobCreateData } from '../hooks/useJobCreateData';
import { getAssignmentRateOverrides } from '../components/create/assignmentRateInputs';
import type { AssignmentRateInputsByWorkerId } from '../components/create/assignmentRateInputs';
import type { AssignmentRateOverrides } from '@core/job/assignmentRateOverrides.type';

const initialValues = {
  companyName: '',
  clientId: '',
  siteId: '',
  address: undefined as Address | undefined,
  coordinates: undefined as LatLng | undefined,
  displayCompanyName: false,
  startDate: '',
  endDate: '',
  startTime: '',
  endTime: '',
  workingDays: [] as DayOfWeek[],
  jobDescription: '',
  workersNeeded: 1,
  totalCost: 0,
  saveAsTemplate: false,
  classification: {} as Classification,
  assignmentMethod: JobAcceptType.Apply as JobAcceptType | 'assign',
};

const validationSchema = Yup.object({
  companyName: Yup.string().required('Required'),
  siteId: Yup.string().required('Site is required'),
  startDate: Yup.string()
    .required('Required')
    .matches(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date'),
  endDate: Yup.string().test(
    'end-date-format',
    'Invalid date',
    (v) => !v || /^\d{4}-\d{2}-\d{2}$/.test(String(v)),
  ),
  startTime: Yup.string().required('Required'),
  endTime: Yup.string()
    .required('Required')
    .test(
      'is-after-start-time',
      'End time must be after start time.',
      function (value) {
        if (!value) return true;
        return isShiftEndAfterStart(this.parent);
      },
    ),
  jobDescription: Yup.string().required('Required'),
  workersNeeded: Yup.number()
    .min(1, 'At least 1 worker')
    .max(10, 'At most 10 workers')
    .required('Required'),
});

const JobCreatePage: React.FC = () => {
  const view = useFeatureView(getJobCreationView);
  const { currentUser } = useUserStore();
  const { createJob, updateJob, assignWorkerToJob } = useJobStore();
  const { ensureCoreLoopConfiguration } = useCoreLoopConfigurationStore();
  const [classificationSearch, setClassificationSearch] = useState('');
  const [assignedWorkers, setAssignedWorkers] = useState<User[]>([]);
  const [assignmentRateInputs, setAssignmentRateInputs] =
    useState<AssignmentRateInputsByWorkerId>({});
  const [draftJobs, setDraftJobs] = useState<DraftJob[]>([]);
  const [availableWorkers, setAvailableWorkers] = useState<User[]>([]);
  const [isLoadingAvailableWorkers, setIsLoadingAvailableWorkers] =
    useState(false);
  const [isPostingJobs, setIsPostingJobs] = useState(false);
  const [activeSitesForClient, setActiveSitesForClient] = useState<Site[]>([]);

  const navigate = usePortalNavigate();
  const theme = useThemeStore();
  const location = useLocation();
  const previousPage = location.state?.from || 'jobs';
  const tenantId = useTenantId();

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: () => {
      // Form submission is handled by postAllJobs button
    },
  });

  const {
    clients,
    classificationGroups,
    isClassificationLoading,
    currentUserClientId,
  } = useJobCreateData(view, formik.values.clientId);
  const {
    isLoading: isQuestionnaireLoading,
    questions: questionnaireQuestions,
    responses: questionnaireResponses,
    updateResponse: updateQuestionnaireResponse,
    clearResponses: clearQuestionnaireResponses,
    getQuestionnaireInput,
    isQuestionnaireComplete,
  } = usePostingQuestionnaire(view.sections.questionnaire, tenantId);

  const { generateAiJobDescription, isGeneratingAi } =
    useGenerateAiJobDescription({
      descriptionField: 'jobDescription',
      setFieldError: formik.setFieldError,
      setFieldValue: formik.setFieldValue,
      getInput: () => {
        const selectedClient = clients.find(
          (c) => c.id === formik.values.clientId,
        );
        const selectedSite = activeSitesForClient.find(
          (site) => site.id === formik.values.siteId,
        );
        return {
          classificationName: formik.values.classification?.name,
          startTime: formik.values.startTime,
          clientName: selectedClient?.name,
          siteAddress: selectedSite?.address,
          siteLabel: selectedSite?.name,
        };
      },
    });

  const setFieldValueRef = useRef(formik.setFieldValue);
  const hasSeededClientRef = useRef(false);

  useEffect(() => {
    if (view.query.clients.strategy !== 'currentUser' || !currentUserClientId) {
      return;
    }
    const client = clients.find((c) => c.id === currentUserClientId);
    if (!client || hasSeededClientRef.current) return;

    hasSeededClientRef.current = true;
    setFieldValueRef.current('clientId', currentUserClientId);
    if (client.name) {
      setFieldValueRef.current('companyName', client.name);
    }
  }, [view.query.clients.strategy, currentUserClientId, clients]);

  useEffect(() => {
    setFieldValueRef.current = formik.setFieldValue;
  }, [formik.setFieldValue]);

  const applySiteToForm = useCallback((site: Site | null) => {
    if (!site) return;
    if (site.address) {
      setFieldValueRef.current('address', site.address);
    }
    if (site.latLng) {
      setFieldValueRef.current('coordinates', site.latLng);
    }
  }, []);

  useEffect(() => {
    setFieldValueRef.current = formik.setFieldValue;
  }, [formik.setFieldValue]);

  useEffect(() => {
    if (tenantId) {
      ensureCoreLoopConfiguration(tenantId);
    }
  }, [ensureCoreLoopConfiguration, tenantId]);

  useEffect(() => {
    if (!formik.values.siteId) return;
    const selectedSite = activeSitesForClient.find(
      (site) => site.id === formik.values.siteId,
    );
    if (selectedSite) {
      applySiteToForm(selectedSite);
    }
  }, [formik.values.siteId, activeSitesForClient, applySiteToForm]);

  const {
    selectableDays: selectableWorkingDays,
    isVisible: showWorkingDays,
    toggleDay: toggleWorkingDay,
    workingDaysSummary,
    daysOffSummary,
    preserveSelectionForSchedule,
  } = useJobWorkingDays({
    startDate: formik.values.startDate,
    endDate: formik.values.endDate,
    selectedDays: formik.values.workingDays,
    onSelectedDaysChange: (days) =>
      setFieldValueRef.current('workingDays', days),
    numberOfWorkers: formik.values.workersNeeded,
  });

  const bookedWorkers = useMemo(() => {
    const bookedWorkers: Record<
      string,
      Array<{ startDate: string; endDate: string }>
    > = {};
    for (const draftJob of draftJobs) {
      if (!draftJob.assignedWorkers) continue;

      const { contractStart, contractEnd } = getJobCreateContractInstants({
        scheduleStartDay: draftJob.startDate,
        scheduleLastEveningDay: draftJob.endDate,
        startTime: draftJob.startTime,
        endTime: draftJob.endTime,
      });

      const range = {
        startDate: String(contractStart.valueOf()),
        endDate: String(contractEnd.valueOf()),
      };

      for (const workerId of draftJob.assignedWorkers) {
        if (!bookedWorkers[workerId]) {
          bookedWorkers[workerId] = [];
        }
        bookedWorkers[workerId].push(range);
      }
    }
    return bookedWorkers;
  }, [draftJobs]);

  const fetchAvailableWorkersForJob = useCallback(
    async (
      classificationId: string | null,
      clientId: string | null,
      startDate: string | null,
      endDate: string | null,
    ) => {
      try {
        const lat = formik.values.coordinates?.lat;
        const lng = formik.values.coordinates?.lng;
        if (!classificationId || !startDate || !endDate || !clientId) return [];

        setIsLoadingAvailableWorkers(true);
        const availableWorkersForJobCircumstances =
          await fetchUsersAvailableForJobCriteria(
            classificationId,
            clientId,
            startDate,
            endDate,
            lat,
            lng,
          );

        if (!availableWorkersForJobCircumstances) {
          setAvailableWorkers([]);
          return;
        }

        // Filter out workers that have draft jobs dates that overlap with
        // the current job dates.
        const availableWorkers = availableWorkersForJobCircumstances.filter(
          (worker) => {
            const bookedDates = bookedWorkers?.[worker.id];

            if (!bookedDates) return true;

            return bookedDates.every((date) => {
              return !isDateRangeOverlapping(
                date.startDate,
                date.endDate,
                startDate,
                endDate,
              );
            });
          },
        );

        setAvailableWorkers(availableWorkers);
      } catch (error) {
        console.error('Error fetching available workers for job:', error);
        setAvailableWorkers([]);
      } finally {
        setIsLoadingAvailableWorkers(false);
      }
    },
    [
      formik.values.coordinates?.lat,
      formik.values.coordinates?.lng,
      bookedWorkers,
    ],
  );

  useEffect(() => {
    if (
      formik.values.classification?.id &&
      formik.values.startDate &&
      formik.values.startTime &&
      formik.values.endTime
    ) {
      const { contractStart, contractEnd } = getJobCreateContractInstants({
        scheduleStartDay: formik.values.startDate,
        scheduleLastEveningDay: formik.values.endDate,
        startTime: formik.values.startTime,
        endTime: formik.values.endTime,
      });

      if (view.query.availableWorkers.fetch) {
        fetchAvailableWorkersForJob(
          formik.values.classification.id,
          formik.values.clientId,
          String(contractStart.valueOf()),
          String(contractEnd.valueOf()),
        );
      }
      return;
    }
    // If we don't have all the required fields, or shift times are invalid, set available workers to empty array.
    // This will prevent the user from selecting a worker for a job with unvalidated data.
    setAvailableWorkers([]);
  }, [
    fetchAvailableWorkersForJob,
    formik.values.classification.id,
    formik.values.clientId,
    formik.values.startDate,
    formik.values.endDate,
    formik.values.startTime,
    formik.values.endTime,
    view.query.availableWorkers.fetch,
  ]);

  const deleteCurrentJob = () => {
    const previousDraftJob: DraftJob | undefined = draftJobs.pop();

    if (previousDraftJob === undefined) {
      // If there are no previous draft jobs, deleting the current
      // job will reset the form.
      formik.resetForm();
      setAssignedWorkers([]);
      setAssignmentRateInputs({});
      return;
    }

    // Populate form with previous draft job data.
    copyDraftJob(previousDraftJob);
  };

  // Draft job functionality
  const getRateOverridesByWorkerId = () =>
    assignedWorkers.reduce<Record<string, AssignmentRateOverrides>>(
      (rateOverridesByWorkerId, assignedWorker) => {
        const rateOverrides = getAssignmentRateOverrides(
          assignmentRateInputs[assignedWorker.id],
        );
        if (rateOverrides) {
          rateOverridesByWorkerId[assignedWorker.id] = rateOverrides;
        }
        return rateOverridesByWorkerId;
      },
      {},
    );

  const addToDraft = () => {
    // Validate form before adding to draft
    if (!formik.isValid || !formik.dirty) {
      toast('Please fill in all required fields before adding to draft', {
        className: 'bg-white text-black',
        position: 'bottom-left',
        autoClose: 3000,
      });
      return;
    }

    let assignmentRateOverridesByWorkerId:
      Record<string, AssignmentRateOverrides> | undefined;
    if (formik.values.assignmentMethod === 'assign') {
      try {
        assignmentRateOverridesByWorkerId = getRateOverridesByWorkerId();
      } catch (error) {
        toast(
          error instanceof Error
            ? error.message
            : 'Enter valid custom assignment rates',
          {
            className: 'bg-white text-black',
            position: 'bottom-left',
            autoClose: 3000,
          },
        );
        return;
      }
    }

    const selectedSite = activeSitesForClient.find(
      (site) => site.id === formik.values.siteId,
    );

    const draftJob: DraftJob = {
      id: `draft-${Date.now()}`,
      clientId: formik.values.clientId,
      companyName: formik.values.companyName,
      siteId: formik.values.siteId,
      siteName: selectedSite?.name,
      address: formik.values.address,
      coordinates: formik.values.coordinates,
      classification: formik.values.classification,
      workersNeeded: formik.values.workersNeeded,
      startDate: formik.values.startDate,
      endDate: formik.values.endDate,
      startTime: formik.values.startTime,
      endTime: formik.values.endTime,
      workingDays: formik.values.workingDays,
      jobDescription: formik.values.jobDescription,
      assignmentMethod: formik.values.assignmentMethod,
      assignedWorkers:
        formik.values.assignmentMethod === 'assign'
          ? assignedWorkers?.map((w) => w.id)
          : undefined,
      assignmentRateOverridesByWorkerId,
      createdAt: new Date(),
    };

    setDraftJobs((prev) => [...prev, draftJob]);

    // Reset form
    formik.resetForm();
    setAssignedWorkers([]);
    setAssignmentRateInputs({});

    toast('Job added to draft', {
      className: 'bg-white text-black',
      position: 'bottom-left',
      autoClose: 2000,
    });
  };

  const copyDraftJob = (draftJob: DraftJob) => {
    // Keep the days the draft was saved with rather than reselecting the whole range.
    preserveSelectionForSchedule(draftJob.startDate, draftJob.endDate);

    // Populate form with draft job data
    formik.setValues({
      companyName: draftJob.companyName,
      address: draftJob.address,
      coordinates: draftJob.coordinates,
      clientId: draftJob.clientId,
      siteId: draftJob.siteId || '',
      displayCompanyName: false,
      startDate: draftJob.startDate,
      endDate: draftJob.endDate,
      startTime: draftJob.startTime,
      endTime: draftJob.endTime,
      workingDays: draftJob.workingDays,
      jobDescription: draftJob.jobDescription,
      workersNeeded: draftJob.workersNeeded,
      totalCost: 0,
      saveAsTemplate: false,
      classification: draftJob.classification,
      assignmentMethod: draftJob.assignmentMethod,
    });

    // Reset the assigned workers as those workers are already assigned to the
    // previous draft job - they are unavailable for a new job with the same times.
    setAssignedWorkers([]);
    setAssignmentRateInputs({});

    toast('Draft job copied to form', {
      className: 'bg-white text-black',
      position: 'bottom-left',
      autoClose: 2000,
    });
  };

  const deleteDraftJob = (draftJobId: string) => {
    setDraftJobs((prev) => prev.filter((job) => job.id !== draftJobId));
    toast('Draft job deleted', {
      className: 'bg-white text-black',
      position: 'bottom-left',
      autoClose: 2000,
    });
  };

  const postDraftJob = async (
    draftJob: DraftJob,
    questionnaireInput?: QuestionnaireInput,
  ) => {
    try {
      toast.dismiss('posting-draft-job');
      toast('Posting draft job...', {
        toastId: 'posting-draft-job',
        autoClose: false,
        className: 'bg-white text-black',
        position: 'bottom-left',
      });

      if (!tenantId) {
        throw new Error('Tenant must be set');
      }

      // Convert draft job to CreateJobInput
      const { contractStart, contractEnd } = getJobCreateContractInstants({
        scheduleStartDay: draftJob.startDate,
        scheduleLastEveningDay: draftJob.endDate,
        startTime: draftJob.startTime,
        endTime: draftJob.endTime,
      });

      const payload = buildCreateJobInput({
        clientId: draftJob.clientId,
        title: draftJob.classification?.name || '',
        description: draftJob.jobDescription,
        classificationId: draftJob.classification?.id || '',
        acceptType:
          draftJob.assignmentMethod === 'assign'
            ? JobAcceptType.Accept
            : draftJob.assignmentMethod,
        contractStart,
        contractEnd,
        workingDays: draftJob.workingDays,
        address: draftJob.address
          ? {
              addressFriendly: generateFriendlyAddress(draftJob.address),
              addressLine1: draftJob.address?.addressLine1 || '',
              addressLine2: draftJob.address?.addressLine2 || '',
              suburb: draftJob.address?.suburb || '',
              state: draftJob.address?.state || '',
              postcode: draftJob.address?.postcode || '',
              countryLabel: draftJob.address?.countryLabel || '',
              countryValue: draftJob.address?.countryValue || '',
            }
          : undefined,
        siteId: draftJob.siteId ?? '',
      });

      // Create jobs based on assignment method
      let jobs = [];
      if (draftJob.assignmentMethod === 'assign' && draftJob.assignedWorkers) {
        // Create one job per assigned worker
        const jobPromises = draftJob.assignedWorkers?.map(() =>
          createJob(
            { ...payload, skipNotifications: true },
            questionnaireInput,
          ),
        );
        const jobResults = await Promise.all(jobPromises);
        jobs = jobResults.filter((job) => job !== null && job !== undefined);

        // Flag assign-flow jobs, then assign workers to their respective jobs
        const assignPromises = jobs.map(async (job, index) => {
          const assignedWorkerId = draftJob.assignedWorkers![index];
          if (!assignedWorkerId) {
            return;
          }
          await assignWorkerToJob(
            job.id,
            assignedWorkerId,
            draftJob.assignmentRateOverridesByWorkerId?.[assignedWorkerId],
          );
          await updateJob(job.id, { workersShouldApplyToJob: true });
        });
        await Promise.all(assignPromises);
      } else {
        // Create multiple jobs based on workersNeeded
        const jobPromises = Array.from({ length: draftJob.workersNeeded }, () =>
          createJob(payload, questionnaireInput),
        );
        const jobResults = await Promise.all(jobPromises);
        jobs = jobResults.filter((job) => job !== null && job !== undefined);
      }

      // Remove the posted draft job
      setDraftJobs((prev) => prev.filter((job) => job.id !== draftJob.id));

      toast.dismiss('posting-draft-job');
      if (jobs.length > 0) {
        const jobCount = jobs.length;
        const jobText = jobCount === 1 ? 'job' : 'jobs';
        toast(`${jobCount} ${jobText} posted successfully!`, {
          className: 'bg-white text-black',
          position: 'bottom-left',
          autoClose: 3000,
          hideProgressBar: true,
        });
      }
    } catch (error) {
      toast.dismiss('posting-draft-job');
      toast(
        error instanceof Error ? error.message : 'Failed to post draft job',
        {
          className: 'bg-white text-black',
          position: 'bottom-left',
        },
      );
    }
  };

  const postAllJobs = async () => {
    if (isPostingJobs) {
      return;
    }

    const hasDraftJobs = draftJobs.length > 0;
    const hasValidForm = formik.isValid && formik.dirty;
    const hasCompleteQuestionnaire = isQuestionnaireComplete();

    if (hasValidForm && formik.values.assignmentMethod === 'assign') {
      try {
        getRateOverridesByWorkerId();
      } catch (error) {
        toast(
          error instanceof Error
            ? error.message
            : 'Enter valid custom assignment rates',
          {
            className: 'bg-white text-black',
            position: 'bottom-left',
            autoClose: 3000,
          },
        );
        return;
      }
    }

    if (!hasDraftJobs && !hasValidForm) {
      toast('No jobs to post', {
        className: 'bg-white text-black',
        position: 'bottom-left',
        autoClose: 3000,
      });
      return;
    }

    if (!hasCompleteQuestionnaire) {
      toast('Please complete all required questionnaire questions', {
        className: 'bg-white text-black',
        position: 'bottom-left',
        autoClose: 3000,
      });
      return;
    }

    setIsPostingJobs(true);

    try {
      setIsPostingJobs(true);
      toast.dismiss('posting-all-jobs');
      toast('Posting jobs...', {
        toastId: 'posting-all-jobs',
        autoClose: false,
        className: 'bg-white text-black',
        position: 'bottom-left',
      });

      let totalJobsCreated = 0;
      const questionnaireInput = view.sections.questionnaire
        ? { responses: getQuestionnaireInput() }
        : undefined;

      // Post all draft jobs sequentially to avoid race conditions
      for (const draftJob of draftJobs) {
        await postDraftJob(draftJob, questionnaireInput);
        totalJobsCreated += draftJob.workersNeeded;
      }

      // Also post the current form if it's valid
      if (hasValidForm) {
        const values = formik.values;

        if (!currentUser?.tenantId) {
          throw new Error('User must be associated with a tenant');
        }

        const startTimeStr = values.startTime || '';
        const endTimeStr = values.endTime || '';

        let startDateTime;
        let endDateTime;
        if (values.startDate && startTimeStr && endTimeStr) {
          const resolved = getJobCreateContractInstants({
            scheduleStartDay: values.startDate,
            scheduleLastEveningDay: values.endDate,
            startTime: startTimeStr,
            endTime: endTimeStr,
          });
          startDateTime = resolved.contractStart;
          endDateTime = resolved.contractEnd;
        }

        if (startDateTime && endDateTime) {
          const payload = buildCreateJobInput({
            clientId: values.clientId || '',
            title: values.classification?.name || '',
            description: values.jobDescription,
            classificationId: values.classification?.id || '',
            acceptType:
              values.assignmentMethod === 'assign'
                ? JobAcceptType.Accept
                : values.assignmentMethod,
            contractStart: startDateTime,
            contractEnd: endDateTime,
            workingDays: values.workingDays,
            address: values.address
              ? {
                  addressFriendly: generateFriendlyAddress(values.address),
                  addressLine1: values.address?.addressLine1 || '',
                  addressLine2: values.address?.addressLine2 || '',
                  suburb: values.address?.suburb || '',
                  state: values.address?.state || '',
                  postcode: values.address?.postcode || '',
                  countryLabel: values.address?.countryLabel || '',
                  countryValue: values.address?.countryValue || '',
                }
              : undefined,
            siteId: values.siteId,
          });

          let jobs = [];
          if (values.assignmentMethod === 'assign') {
            if (assignedWorkers?.length === values.workersNeeded) {
              const jobPromises = assignedWorkers?.map(() =>
                createJob(
                  { ...payload, skipNotifications: true },
                  questionnaireInput,
                ),
              );
              const jobResults = await Promise.all(jobPromises);
              jobs = jobResults.filter(
                (job) => job !== null && job !== undefined,
              );

              const assignPromises = jobs.map(async (job, index) => {
                const assignedWorker = assignedWorkers[index];
                if (!assignedWorker) {
                  return;
                }
                await assignWorkerToJob(
                  job.id,
                  assignedWorker.id,
                  getAssignmentRateOverrides(
                    assignmentRateInputs[assignedWorker.id],
                  ),
                );
                // Set after assign
                await updateJob(job.id, { workersShouldApplyToJob: true });
              });
              await Promise.all(assignPromises);
            }
          } else {
            const jobPromises = Array.from(
              { length: values.workersNeeded },
              () => createJob(payload, questionnaireInput),
            );
            const jobResults = await Promise.all(jobPromises);
            jobs = jobResults.filter(
              (job) => job !== null && job !== undefined,
            );
          }

          totalJobsCreated += jobs.length;

          // Reset form after successful submission
          formik.resetForm();
          setAssignedWorkers([]);
          setAssignmentRateInputs({});
        }
      }

      toast.dismiss('posting-all-jobs');
      if (totalJobsCreated > 0) {
        clearQuestionnaireResponses();
        const jobText = totalJobsCreated === 1 ? 'job' : 'jobs';
        toast(`${totalJobsCreated} ${jobText} posted successfully!`, {
          className: 'bg-white text-black',
          position: 'bottom-left',
          autoClose: 3000,
          hideProgressBar: true,
        });
        navigate('/jobs');
      } else {
        toast('No jobs were created', {
          className: 'bg-white text-black',
          position: 'bottom-left',
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.dismiss('posting-all-jobs');
      toast(error instanceof Error ? error.message : 'Failed to post jobs', {
        className: 'bg-white text-black',
        position: 'bottom-left',
      });
    } finally {
      setIsPostingJobs(false);
    }
  };

  return (
    <div className="px-2 py-4">
      {/* Top Navigation */}
      <div className="mx-auto mb-6 flex max-w-2xl items-center justify-between">
        <Link
          to={'/' + previousPage}
          className="flex items-center text-sm text-gray-700 hover:underline"
        >
          &larr; Back to {previousPage}
        </Link>
      </div>

      {/* Draft Jobs Section */}
      {draftJobs.length > 0 && (
        <div className="mx-auto mb-6 max-w-3xl">
          <div className="mb-4 text-lg font-semibold">Draft Jobs</div>
          <div className="space-y-3">
            {draftJobs.map((draftJob) => (
              <DraftJobCard
                key={draftJob.id}
                draftJob={draftJob}
                onCopy={copyDraftJob}
                onDelete={deleteDraftJob}
                clients={clients.map((c) => ({
                  id: c.id,
                  name: c.name || '',
                  address: c.address
                    ? {
                        suburb: c.address.suburb || undefined,
                        state: c.address.state || undefined,
                      }
                    : undefined,
                }))}
              />
            ))}
          </div>
        </div>
      )}

      {/* Main Card */}
      <div className="mx-auto max-w-3xl rounded-xl bg-white p-8 shadow-sm">
        <form onSubmit={formik.handleSubmit}>
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div className="mb-4 text-lg font-semibold">
                Enter Job Details
              </div>
              <button
                onClick={() => deleteCurrentJob()}
                className="rounded p-2 text-gray-500 hover:bg-gray-200 hover:text-red-600"
                title="Delete job"
              >
                <FontAwesomeIcon icon={faTrashCan} className="h-4 w-4" />
              </button>
            </div>
            {/* Flexible Row Layout */}
            <div className="mb-4 space-y-6">
              {/* Row 1: Client Selection */}
              {view.actions.selectClient && (
                <div className="grid grid-cols-1 gap-6">
                  <ClientSelector
                    value={formik.values.clientId}
                    onChange={(value) => {
                      formik.setFieldValue('clientId', value);
                      formik.setFieldValue('siteId', '');
                      formik.setFieldValue('address', undefined);
                      formik.setFieldValue('coordinates', undefined);
                      const selectedClient = clients.find(
                        (client) => client.id === value,
                      );
                      formik.setFieldValue(
                        'companyName',
                        selectedClient?.name || '',
                      );
                    }}
                    clients={clients}
                    error={formik.errors.clientId}
                    touched={formik.touched.clientId}
                  />
                </div>
              )}

              {/* Row 2: Client Site Selection */}
              <div className="grid grid-cols-1 gap-6">
                <ClientSiteSelector
                  clientId={formik.values.clientId}
                  value={formik.values.siteId}
                  onChange={(siteId) => formik.setFieldValue('siteId', siteId)}
                  onSiteSelected={applySiteToForm}
                  onActiveSitesChange={setActiveSitesForClient}
                  error={
                    typeof formik.errors.siteId === 'string'
                      ? formik.errors.siteId
                      : undefined
                  }
                  touched={formik.touched.siteId}
                />
              </div>

              {/* Row 3: Skills and Workers */}
              <div className="grid grid-cols-2 gap-6">
                <SkillSetSelector
                  value={formik.values.classification}
                  onChange={(value) => {
                    formik.setFieldValue('classification', value);
                  }}
                  classificationGroups={classificationGroups}
                  isLoading={isClassificationLoading}
                  searchValue={classificationSearch}
                  onSearchChange={setClassificationSearch}
                  error={formik.errors.classification?.name}
                  touched={!!formik.touched.classification}
                />

                <WorkersCounter
                  value={formik.values.workersNeeded}
                  onChange={(value) =>
                    formik.setFieldValue('workersNeeded', value)
                  }
                  error={formik.errors.workersNeeded}
                  touched={formik.touched.workersNeeded}
                />
              </div>

              {/* Row 4: Date and working days */}
              <div className="grid grid-cols-2 gap-6 pt-6">
                <DateSelector
                  startDate={
                    formik.values.startDate
                      ? parseScheduleDateLocal(
                          formik.values.startDate as string | Date,
                        ).toDate()
                      : null
                  }
                  endDate={
                    formik.values.endDate
                      ? parseScheduleDateLocal(
                          formik.values.endDate as string | Date,
                        ).toDate()
                      : null
                  }
                  onDateChange={(startDate, endDate) => {
                    formik.setFieldValue(
                      'startDate',
                      dayjs(startDate).format('YYYY-MM-DD'),
                    );
                    formik.setFieldValue(
                      'endDate',
                      dayjs(endDate).format('YYYY-MM-DD'),
                    );
                  }}
                  primaryBrandColor={theme?.theme?.primaryBrandColor}
                  startDateError={formik.errors.startDate}
                  endDateError={formik.errors.endDate}
                  onBlur={() => {
                    formik.handleBlur({ target: { name: 'startDate' } });
                  }}
                  touched={!!formik.touched.startDate}
                />

                {showWorkingDays && (
                  <WorkingDaysSelector
                    selectableDays={selectableWorkingDays}
                    selectedDays={formik.values.workingDays}
                    onToggleDay={toggleWorkingDay}
                  />
                )}
              </div>

              {showWorkingDays && (
                <p className="text-sm text-gray-500">
                  {`Working days: ${workingDaysSummary}.${daysOffSummary}`}
                </p>
              )}

              {/* Row 5: Shift times */}
              <div className="grid grid-cols-3 gap-6">
                <TimeSelectors
                  time={formik.values.startTime || ''}
                  onTimeChange={(val) => formik.setFieldValue('startTime', val)}
                  timeError={formik.errors.startTime || ''}
                  label="Start time"
                  formikId="startTime"
                  handleBlur={formik.handleBlur}
                  touched={!!formik.touched.startTime}
                />

                <TimeSelectors
                  time={formik.values.endTime || ''}
                  onTimeChange={(val) => formik.setFieldValue('endTime', val)}
                  timeError={formik.errors.endTime || ''}
                  label="End time"
                  formikId="endTime"
                  handleBlur={formik.handleBlur}
                  touched={!!formik.touched.endTime}
                />

                <div className="min-w-0">
                  <div className="mb-1 text-sm font-medium">Duration</div>
                  <div className="border border-transparent px-3 py-2 text-sm text-gray-500">
                    {formatShiftClockDurationHhMm(
                      formik.values.startTime,
                      formik.values.endTime,
                    ) ?? ''}
                  </div>
                </div>
              </div>
            </div>
            <JobDescription
              value={formik.values.jobDescription}
              onChange={(value) =>
                formik.setFieldValue('jobDescription', value)
              }
              onBlur={formik.handleBlur}
              onGenerateAi={generateAiJobDescription}
              isGeneratingAi={isGeneratingAi}
              error={formik.errors.jobDescription}
              touched={formik.touched.jobDescription}
            />
            {view.sections.questionnaire && (
              <QuestionnaireSection
                questions={questionnaireQuestions}
                responses={questionnaireResponses}
                updateResponse={updateQuestionnaireResponse}
                isLoading={isQuestionnaireLoading}
              />
            )}
            <AssignmentMethod
              value={formik.values.assignmentMethod}
              onChange={(value) =>
                formik.setFieldValue('assignmentMethod', value)
              }
              canAssignWorker={view.actions.assignWorker}
            />
            {view.actions.assignWorker && (
              <AssignWorker
                show={formik.values.assignmentMethod === 'assign'}
                assignedWorkers={assignedWorkers}
                setAssignedWorkers={setAssignedWorkers}
                limit={formik.values.workersNeeded}
                workers={availableWorkers}
                isLoading={isLoadingAvailableWorkers}
                clientId={formik.values.clientId}
                classificationId={formik.values.classification?.id ?? ''}
                assignmentRateInputs={assignmentRateInputs}
                setAssignmentRateInputs={setAssignmentRateInputs}
              />
            )}
          </div>
          {formik.status && (
            <div className="mb-4 rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-800">{formik.status}</div>
            </div>
          )}
        </form>
      </div>

      {/* Add New Job Button */}
      <div className="mx-auto mt-6 flex max-w-3xl justify-center">
        <Button
          variant="primary"
          onClick={addToDraft}
          disabled={!formik.isValid || !formik.dirty}
          className="space-x-2"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          <span>Add new job</span>
        </Button>
      </div>

      {/* Post All Jobs Button */}
      <div className="mx-auto mt-6 flex max-w-3xl justify-end">
        <Button
          variant="primary"
          onClick={postAllJobs}
          disabled={
            isPostingJobs ||
            (draftJobs.length === 0 && !(formik.isValid && formik.dirty)) ||
            !isQuestionnaireComplete()
          }
          isLoading={isPostingJobs}
        >
          {isPostingJobs
            ? 'Posting…'
            : `Post ${draftJobs.length > 0 ? 'all jobs' : 'job'}`}
        </Button>
      </div>
    </div>
  );
};

export default JobCreatePage;
